import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../stores/store';

import ParcelParticipantPersonalFormDynamic from '../../features/parcel-creation/parcel-participant-personal/parcel-participant-personal-form';
import ParcelParticipantContactForm from '../../features/parcel-creation/parcel-participant-contact/parcel-participant-contact.component';
import ParcelParticipantPudoForm from '../../features/parcel-creation/parcel-participant-pudo/parcel-participant-pudo.component';
import DeliveryDetailsForm, {
  UserRole,
} from '../../features/parcel-creation/delivery-details-form/delivery-details-form.component';
import { removeCountryCallingCodeFromPhone } from '../personal-data/contact-details-form.component';
import CallCourier from '../../features/parcel-creation/call-courier/call-courier.component';
import { DEFAULT_COUNTRY } from '../../stores/localizationsStore';
import useSenderTip from './use-sender-tip.hook';
import { isFeatureRequired } from '../../utils/client/client.utils';

const ParcelSender = () => {
  const {
    parcelCreationStore: {
      selectedSenderPudo,
      shipment,
      countryDeparture,
      getContactDetailsValidationSchema,
      navigation: { current },
      setSenderPersonalDataFormValues,
      setIsSenderPersonalDataValid,
      setSenderPhone,
      setSenderPhoneCountryCode,
      setSenderEmail,
      setSenderPudo,
    },
    userStore: { user },
    localizationsStore: { availableCountries },
  } = useStore();

  const { t } = useTranslation();

  const senderTip = useSenderTip();

  if (!shipment) return null;

  const initialValuesContactForm = {
    phone:
      removeCountryCallingCodeFromPhone(
        shipment?.sender?.phone?.phone_number,
        shipment?.sender?.phone?.country_code
      ) ??
      removeCountryCallingCodeFromPhone(
        user?.phone?.phone_number,
        user?.phone?.country_code
      ) ??
      '',
    phone_country_code:
      shipment?.sender?.phone?.country_code ??
      user?.phone?.country_code.toUpperCase() ??
      countryDeparture?.code ??
      DEFAULT_COUNTRY.country_code,
    email: shipment.sender?.email ?? user?.email ?? '',
  };

  const isPudo = current?.name === 'sender-details-pudo';
  const isConcierge = current?.name === 'sender-details-concierge';

  const shouldBlockPhone = isFeatureRequired(
    'recipientPhoneNumberBlocked',
    countryDeparture?.code
  );

  if (!countryDeparture) return;

  const allowedPhoneCountries = shouldBlockPhone
    ? [countryDeparture]
    : availableCountries?.map((x) => x.countryDetails) ?? [];

  return (
    <>
      <ParcelParticipantPersonalFormDynamic
        key='sender_personal_form'
        setGlobalStateFormValues={setSenderPersonalDataFormValues}
        setGlobalStateFormIsValid={setIsSenderPersonalDataValid}
      />

      <ParcelParticipantContactForm
        initialValues={initialValuesContactForm}
        validationSchema={getContactDetailsValidationSchema('sender')}
        storeParticipantPhone={setSenderPhone}
        storeParticipantPhoneCountryCode={setSenderPhoneCountryCode}
        storeParticipantEmail={setSenderEmail}
        defaultCountryCode={countryDeparture?.code}
        isDropdownDisabled={shouldBlockPhone}
        allowedPhoneCountries={allowedPhoneCountries}
      />
      {isPudo ? (
        <>
          <DeliveryDetailsForm
            role={UserRole.sender}
            isHomeAddress
          />
          <ParcelParticipantPudoForm
            initialValues={{
              pudo: selectedSenderPudo,
            }}
            tip={senderTip}
            role={UserRole.sender}
            header={t('drop_off_address', {
              carrierName: shipment.sender?.carrier.delivery_carrier_name,
            })}
            storeRecipientPudo={setSenderPudo}
          />
        </>
      ) : (
        <>
          <DeliveryDetailsForm role={UserRole.sender} />
          {!isConcierge && (
            <CallCourier
              // key is required here to re-render when reschedule modal changes state
              key={`${shipment.sender?.courier_pickup?.date} ${shipment.sender?.courier_pickup?.time_from} ${shipment.sender?.courier_pickup?.time_to}`}
            />
          )}
        </>
      )}
    </>
  );
};

export default observer(ParcelSender);
